// FILE: src/config/bankTargets.js
//
// Question-bank stocking targets for the Question Factory.
//
// The Question Factory hard-restricts generation to UNDERSTOCKED gaps: an
// admin may only generate questions for a certification (and a category
// within it) whose live/active inventory sits below its blueprint target.
// Well-stocked certs and categories are blocked so generation effort is
// always spent where the bank is thin.
//
// Targets are DERIVED, not hand-maintained:
//
//     certTarget(examId)      = fullExam.totalQuestions * BANK_COVERAGE_MULTIPLIER
//     categoryTarget(cat)     = round(blueprintPct(cat) / 100 * certTarget)
//
// `categoryDistribution` (src/config/exams.js) is the single source of truth
// for the blueprint weights, and `EXAM_CATEGORIES` (src/config/examCategories.js)
// for the canonical category vocabulary. To re-tune stocking depth, change
// BANK_COVERAGE_MULTIPLIER here; to re-tune the per-exam mix, edit the
// blueprint in exams.js. No component change is required.
//
// Pure module — no Supabase, no React, no side effects. Both the browser
// (GenerationForm) and Node test code import it.
//
// NOTE (migration 107): the targets here are the BASELINE floor — operating
// inventory derived from exam length. Demand-driven expansion above that floor
// is computed in SQL (`qf_generation_priority`) from learner exhaustion,
// exposure, learner count and blueprint deficits, so the overnight workflow and
// the admin Generation panel share one definition. Keep this module for the
// baseline/manual-form path; do not re-implement the demand model here.

import { getExamConfig, getAllExams } from './exams';
import { EXAM_CATEGORIES } from './examCategories';

// ──────────────────────────────────────────────────────────────────────
// Tunables
// ──────────────────────────────────────────────────────────────────────

/**
 * How many full exams' worth of questions a bank should hold per cert.
 * A multiplier of 3 means "three unique full exams of depth" — e.g. a
 * 120-question exam targets a 360-question bank. Mirrors the depth used by
 * the Admin bank-health coverage view.
 *
 * SCOPE (narrowed by migration 107): this is the BASELINE floor only — the
 * operating inventory a cert needs to function, derived from exam length. It
 * is deliberately blind to demand, which is why it is no longer the whole
 * stocking rule.
 *
 * Above this floor, further generation is governed by the demand-aware model
 * in `qf_generation_priority` (exhaustion, exposure, learner count, blueprint
 * gaps, unresolved-queue brake) — NOT by this multiplier. Raising it does not
 * commission more questions for a cert that is already above baseline.
 *
 * The distinction matters: judged by this multiplier alone, CPMAI read as
 * "stocked" at 360 while its learners were re-seeing every question ~75 times,
 * and PMI-ACP read as fine at 8.8x target with one external learner.
 */
export const BANK_COVERAGE_MULTIPLIER = 3;

// ──────────────────────────────────────────────────────────────────────
// Target computation
// ──────────────────────────────────────────────────────────────────────

/**
 * Total target inventory (active questions) for a cert's bank.
 * @param {string} examId
 * @returns {number} target count, or 0 when the cert/blueprint is unknown.
 */
export function getCertTargetTotal(examId) {
  const cfg = getExamConfig(examId);
  const full = cfg && cfg.fullExam && Number(cfg.fullExam.totalQuestions);
  if (!full || !Number.isFinite(full)) return 0;
  return Math.round(full * BANK_COVERAGE_MULTIPLIER);
}

/**
 * Per-category target counts for a cert, derived from its blueprint
 * distribution. Categories are restricted to the canonical vocabulary in
 * EXAM_CATEGORIES so off-blueprint values never receive a target.
 *
 * @param {string} examId
 * @returns {Object<string, number>} { [category]: targetCount }
 */
export function getCategoryTargets(examId) {
  const cfg = getExamConfig(examId);
  const dist = (cfg && cfg.categoryDistribution) || {};
  const certTarget = getCertTargetTotal(examId);
  const canonical = EXAM_CATEGORIES[examId] || [];

  // Sum only the weights of canonical categories so the fractions are
  // normalised against the known vocabulary (defensive — blueprint and
  // vocabulary are expected to match).
  const totalWeight = canonical.reduce((sum, cat) => sum + (Number(dist[cat]) || 0), 0);
  if (totalWeight <= 0 || certTarget <= 0) {
    return Object.fromEntries(canonical.map((cat) => [cat, 0]));
  }

  const out = {};
  for (const cat of canonical) {
    const weight = Number(dist[cat]) || 0;
    out[cat] = Math.round((weight / totalWeight) * certTarget);
  }
  return out;
}

// ──────────────────────────────────────────────────────────────────────
// Gap analysis
// ──────────────────────────────────────────────────────────────────────

/**
 * @typedef {Object} CategoryGap
 * @property {string}  category
 * @property {number}  target
 * @property {number}  active
 * @property {number}  gap          - max(0, target - active)
 * @property {boolean} understocked - active < target
 */

/**
 * @typedef {Object} CertGap
 * @property {string}        examId
 * @property {number}        targetTotal
 * @property {number}        activeTotal
 * @property {number}        totalGap      - max(0, targetTotal - activeTotal)
 * @property {boolean}       understocked  - activeTotal < targetTotal
 * @property {CategoryGap[]} categories    - one per canonical category
 */

/**
 * Compute the gap picture for a single cert against an active-inventory
 * snapshot.
 *
 * @param {string} examId
 * @param {{ total?: number, byCategory?: Object<string,number> }} [inventory]
 * @returns {CertGap}
 */
export function computeCertGap(examId, inventory = {}) {
  const targetTotal = getCertTargetTotal(examId);
  const activeTotal = Number(inventory.total) || 0;
  const catTargets = getCategoryTargets(examId);
  const byCategory = inventory.byCategory || {};

  const categories = Object.keys(catTargets).map((category) => {
    const target = catTargets[category];
    const active = Number(byCategory[category]) || 0;
    return {
      category,
      target,
      active,
      gap: Math.max(0, target - active),
      understocked: active < target,
    };
  });

  return {
    examId,
    targetTotal,
    activeTotal,
    totalGap: Math.max(0, targetTotal - activeTotal),
    understocked: activeTotal < targetTotal,
    categories,
  };
}

/**
 * Compute gaps for every configured cert and return them ranked by total
 * shortfall (largest gap first). Certs at/above target are still included
 * (with understocked=false) so the UI can show them as blocked.
 *
 * @param {Object<string, { total?: number, byCategory?: Object }>} inventoryByCert
 * @returns {CertGap[]}
 */
export function rankCertGaps(inventoryByCert = {}) {
  const examIds = getAllExams().map((e) => e.examId);
  return examIds
    .map((examId) => computeCertGap(examId, inventoryByCert[examId] || {}))
    .sort((a, b) => b.totalGap - a.totalGap);
}

const bankTargets = {
  BANK_COVERAGE_MULTIPLIER,
  getCertTargetTotal,
  getCategoryTargets,
  computeCertGap,
  rankCertGaps,
};

export default bankTargets;
